/* Admin · Apex 2.0 — every AI call across every workspace.
 *
 * Endpoint: GET /api/admin/apex/calls
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, Phone, MessageSquare, ExternalLink, Sparkles, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function AdminApexCalls() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openCall, setOpenCall] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/apex/calls?limit=100");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="animate-spin text-indigo-600" />
      </div>
    );
  }
  const stats = data || {};
  const calls = stats.calls || [];

  return (
    <div className="flex flex-col gap-6" data-testid="admin-apex-calls">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-extrabold text-stone-900 leading-tight flex items-center gap-2">
            <Phone size={20} className="text-indigo-600" /> Apex 2.0 — Call log
          </h1>
          <p className="text-stone-500 text-sm mt-0.5">
            Every inbound seller call answered by Apex AI across the network. Click a row to view the transcript + AI summary.
          </p>
        </div>
        <button type="button" onClick={load} className="btn-outline flex items-center gap-1.5" data-testid="admin-apex-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Health strip */}
      <div className="grid grid-cols-3 gap-3" data-testid="admin-apex-stats">
        <StatTile label="Total AI calls" value={stats.total || 0} accent="#6366F1" icon={Phone} />
        <StatTile label="Completed" value={stats.completed || 0} accent="#10B981" icon={CheckCircle2} />
        <StatTile label="Leads created" value={stats.leads_created || 0} accent="#F59E0B" icon={Sparkles} />
      </div>

      <div className="card p-5" data-testid="admin-apex-list">
        <div className="font-display font-bold text-lg mb-3">Recent calls</div>
        {calls.length === 0 ? (
          <div className="text-sm text-[var(--ink-3)] text-center py-8">
            No AI calls yet — once a wholesaler enables Apex in <code>Settings → Apex 2.0</code>, calls will land here.
          </div>
        ) : (
          <div className="space-y-1.5">
            {calls.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setOpenCall(c)}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-[var(--line)] hover:bg-indigo-50/40 hover:border-indigo-300 transition-colors text-left"
                data-testid={`admin-apex-row-${c.id}`}
              >
                <div className="shrink-0 w-9 h-9 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center">
                  <Phone size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">
                    {c.from_number || "—"} → {c.to_number || "—"}
                  </div>
                  <div className="text-[11px] text-[var(--ink-3)] truncate">
                    {c.started_at?.slice(0, 19).replace("T", " ")} · {c.transcript?.length || 0} turns · {c.status}
                  </div>
                  {c.ai_summary && (
                    <div className="text-[12px] text-stone-700 mt-1 italic line-clamp-1">"{c.ai_summary}"</div>
                  )}
                </div>
                {c.lead_id && (
                  <Link
                    to={`/app/leads/${c.lead_id}/report`}
                    className="shrink-0 text-[11px] font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 hover:underline flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Lead <ExternalLink size={10} />
                  </Link>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {openCall && <CallDrawer call={openCall} onClose={() => setOpenCall(null)} />}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, accent }) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: accent }}>
          <Icon size={18} />
        </div>
        <div>
          <div className="label-eyebrow">{label}</div>
          <div className="font-display font-extrabold text-2xl tabular-nums mt-0.5">{value.toLocaleString()}</div>
        </div>
      </div>
    </div>
  );
}

function CallDrawer({ call, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end md:items-center justify-center p-4" onClick={onClose} data-testid="admin-apex-drawer">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[var(--line)] sticky top-0 bg-white flex items-center justify-between">
          <div>
            <div className="label-eyebrow flex items-center gap-1"><Phone size={11} /> Apex call</div>
            <div className="font-display font-bold text-lg mt-0.5">{call.from_number || "—"} → {call.to_number || "—"}</div>
            <div className="text-xs text-[var(--ink-3)]">{call.started_at?.slice(0, 19).replace("T", " ")} · status: {call.status}</div>
          </div>
          <button type="button" className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>

        {call.ai_summary && (
          <div className="p-4 bg-indigo-50/60 border-b border-indigo-100">
            <div className="label-eyebrow flex items-center gap-1.5"><Sparkles size={11} className="text-indigo-600" /> AI summary</div>
            <div className="text-sm mt-1 italic">"{call.ai_summary}"</div>
            {call.slots && (
              <div className="text-[11px] text-[var(--ink-3)] mt-2 grid grid-cols-2 gap-x-3">
                <div><strong>Address:</strong> {call.slots.property_address || "—"}</div>
                <div><strong>Motivation:</strong> {call.slots.motivation || "—"}</div>
                <div><strong>Timeline:</strong> {call.slots.timeline || "—"}</div>
                <div><strong>Asking:</strong> {call.slots.asking_price ? `$${Number(call.slots.asking_price).toLocaleString()}` : "—"}</div>
                <div className="col-span-2"><strong>Condition:</strong> {call.slots.condition || "—"}</div>
              </div>
            )}
          </div>
        )}

        <div className="p-5 space-y-2">
          <div className="label-eyebrow flex items-center gap-1.5"><MessageSquare size={11} /> Transcript</div>
          {(call.transcript || []).map((t, i) => (
            <div key={i} className={`p-2.5 rounded-lg text-sm ${t.role === "assistant" ? "bg-indigo-50 border border-indigo-100" : "bg-slate-50 border border-slate-100"}`}>
              <div className="text-[10px] uppercase tracking-wider font-bold opacity-60 mb-0.5">
                {t.role === "assistant" ? "Apex" : "Seller"}
              </div>
              <div>{t.text}</div>
            </div>
          ))}
          {(call.transcript || []).length === 0 && (
            <div className="text-sm text-[var(--ink-3)]">No turns recorded.</div>
          )}
        </div>
      </div>
    </div>
  );
}
